import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import { CHAT_DESTINATION_FIELDS, GET_CHAT } from '../../graphql/chats/queries';
import {
  chatRecordRoute,
  inboxThreadRoute,
  type ChatDestination,
} from '../../utils/chatRecordRoute';

/**
 * Turns a chat id into the page that chat should open.
 *
 * A message notification only ever carries the chat's id — the backend puts
 * `chatId` and the sender's details in the payload and nothing that names the
 * job, estimate, invoice or appointment the thread hangs off. So the chat is
 * looked up: out of the cache when the inbox or a list has already loaded it,
 * off the API when it has not, and the inbox thread is the answer when neither
 * works or when the chat is a direct one.
 */
export const useChatDestination = () => {
  const client = useApolloClient();

  return useCallback(
    async (chatId: string): Promise<string> => {
      const fallback = inboxThreadRoute(chatId);

      const cacheId = client.cache.identify({ __typename: 'Chat', id: chatId });
      const cached = cacheId
        ? client.cache.readFragment<ChatDestination>({
            id: cacheId,
            fragment: CHAT_DESTINATION_FIELDS,
          })
        : null;
      if (cached) return chatRecordRoute(cached) ?? fallback;

      try {
        const { data } = await client.query<{ getChat?: { chat?: ChatDestination } | null }>({
          query: GET_CHAT,
          variables: { id: chatId },
          fetchPolicy: 'cache-first',
        });
        return chatRecordRoute(data?.getChat?.chat) ?? fallback;
      } catch {
        return fallback;
      }
    },
    [client]
  );
};
